import { useEffect, useState } from 'react';
import { X, Link, Globe } from 'lucide-react';
import { PLATFORM_OPTIONS } from '../types/socialMedia.types';

export const SocialMediaFormModal = ({ isOpen, onClose, onSubmit, isLoading, initialData = null }) => {
  const [form, setForm] = useState({ platform: '', link: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && initialData) {
      setForm({ platform: initialData.platform, link: initialData.link });
    } else if (!isOpen) {
      setForm({ platform: '', link: '' });
      setErrors({});
    }
  }, [isOpen, initialData]);

  const validate = () => {
    const e = {};
    if (!form.platform) e.platform = 'Platform is required';
    if (!form.link) e.link = 'Link is required';
    else if (!/^https?:\/\/.+/.test(form.link)) e.link = 'Must be a valid URL (https://...)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) onSubmit(form);
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div className='absolute inset-0 bg-black/60 backdrop-blur-sm' onClick={onClose} />
      <div className='relative z-10 w-full max-w-md mx-4 bg-card border border-border rounded-xl shadow-2xl'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-border'>
          <div className='flex items-center gap-2'>
            <Globe size={20} className='text-primary' />
            <h2 className='text-lg font-semibold text-foreground'>
              {initialData ? 'Edit Social Media Link' : 'Add Social Media Link'}
            </h2>
          </div>
          <button onClick={onClose} className='p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors'>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className='px-6 py-5 space-y-4'>
          <div>
            <label className='block text-sm font-medium text-foreground mb-1.5'>
              Platform <span className='text-red-400'>*</span>
            </label>
            <select
              value={form.platform}
              onChange={(e) => setForm((p) => ({ ...p, platform: e.target.value }))}
              className='w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary'
            >
              <option value=''>Select a platform</option>
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.icon} {opt.label}
                </option>
              ))}
            </select>
            {errors.platform && <p className='mt-1 text-xs text-red-400'>{errors.platform}</p>}
          </div>

          <div>
            <label className='block text-sm font-medium text-foreground mb-1.5'>
              Link <span className='text-red-400'>*</span>
            </label>
            <div className='relative'>
              <Link className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground' size={16} />
              <input
                type='url'
                placeholder='https://...'
                value={form.link}
                onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))}
                className='w-full pl-9 pr-4 py-2 bg-input border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary'
              />
            </div>
            {errors.link && <p className='mt-1 text-xs text-red-400'>{errors.link}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-end gap-3 px-6 py-4 border-t border-border'>
          <button onClick={onClose} className='px-4 py-2 text-sm text-muted-foreground border border-border rounded-md hover:bg-accent transition-colors'>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {isLoading ? 'Saving...' : initialData ? 'Save Changes' : 'Add Link'}
          </button>
        </div>
      </div>
    </div>
  );
};